import { LitElement, PropertyValueMap, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { FavoriteController } from '../controllers/favorites-controller';
import { sendEventAddFavorite, sendEventFavoriteButtonClick, sendEventRemoveFavorite } from '../utils/analytics';
import { AuthController } from '../controllers/auth-controller';
import { NotificationsController } from '../controllers/notifications-controller';
import { showLoginModal } from '../utils/loginModal';

@customElement('favorite-button')
export class FavoriteButton extends LitElement {
  authController = new AuthController(this);
  favoriteController = new FavoriteController(this, this.authController);
  notificationController = new NotificationsController(this, this.authController);

  createRenderRoot() {
    return this;
  }

  @property() contentId: string = '';
  @property({ type: Number }) minTimeSuggestion: number = 5000;
  @property({ type: Number }) showSuggestionTime: number = 15000;
  @property() tooltipPosition: string = "bottom";
  @property() addMessage: string = "Adicionar aos favoritos";
  @property() addShortMessage: string = "Salvar";
  @property() addedMessage: string = "Adicionado aos favoritos";
  @property() removeMessage: string = "Remover dos favoritos";
  @property() removedMessage: string = "Removido dos favoritos";
  
  @state() isFavorite: boolean = false;
  @state() firstUpdatedFromApi = false;
  @state() updateFavoriteRequestId: string | undefined;
  @state() checkIsFavoriteRequestId: string | undefined;
  @state() showingTooltip = false;
  @state() tooltipMessage = this.addMessage;
  @state() animationRunning = false;

  get _favoriteButton(): HTMLElement | null {
    return this.renderRoot.querySelector(`#favorite-button-${this.id}`) as HTMLElement;
  }

  get _checkIsFavoriteRequestStatus() {
    return this.favoriteController.checkIsFavoriteRequestStatus(this.checkIsFavoriteRequestId);
  }

  get _updateFavoriteRequestStatus() {
    return this.favoriteController.updateFavoriteRequestStatus(this.updateFavoriteRequestId);
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);

    if (!this.authController.isLogged) {
      this.classList.remove('skeleton');
      return;
    }
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(_changedProperties);
    
    if (!this.authController.isLogged) {
      this.firstUpdatedFromApi = false;
      this.checkIsFavoriteRequestId = undefined;
      return;
    }

    if (!this.firstUpdatedFromApi && !this.checkIsFavoriteRequestId) {
      this.requestInitialStateFromApi();
    }

    this.checkValueUpdatedFromApi();
    
    this._favoriteButton?.classList.toggle('favorite-active', this.isFavorite);
  }

  checkValueUpdatedFromApi() {
    if (this._updateFavoriteRequestStatus?.completed && !this.animationRunning) {
      const apiResponseIsFavorite = this.favoriteController.isArticleFavorite(this.contentId);
      this.updateFavoriteRequestId = undefined;
      this.showingTooltip = false;

      if (!this.isFavorite && !apiResponseIsFavorite) {
        sendEventRemoveFavorite(this.contentId);
      }
      
      if (this.isFavorite && apiResponseIsFavorite) {
        sendEventAddFavorite(this.contentId);
        this.notificationController.getPendingNotifications();
      }

      if (this.isFavorite !== apiResponseIsFavorite) {
        this.animationRunning = true;
        this._favoriteButton?.classList.add(...this.getTransitionClasses(apiResponseIsFavorite));
      }

      this.isFavorite = apiResponseIsFavorite;
      setTimeout(() => {
        if (this.animationRunning) return;
        this.tooltipMessage = apiResponseIsFavorite ? this.removeMessage : this.addMessage
      }, 500);
    }

    if (!this.firstUpdatedFromApi && this._checkIsFavoriteRequestStatus?.completed) {
      this.firstUpdatedFromApi = true;
      this.isFavorite = this.favoriteController.isArticleFavorite(this.contentId);
      this.classList.remove('skeleton');

      if (!this.isFavorite) {
        this.tooltipMessage = this.addMessage;
      }

      if (this.isFavorite) {
        this.tooltipMessage = this.removeMessage;
        if (this.showingTooltip) this.clearTooltip();
      }
    }
  }

  async requestInitialStateFromApi() {
    const { requestId } = this.favoriteController.checkIsFavorite(this.contentId);
    this.checkIsFavoriteRequestId = requestId;
  }

  getTransitionClasses(isFavorite?: boolean): string[] {
    if (isFavorite === undefined) return ['favorite-transition', 'favorite-transition--added', 'favorite-transition--removed'];
    const output = ['favorite-transition'];
    if (isFavorite) output.push('favorite-transition--added');
    if (!isFavorite) output.push('favorite-transition--removed');
    return output;
  }

  clearTooltip() {
    if (this.animationRunning) return;
    this.showingTooltip = false;
  }

  clearAnimation() {
    this.animationRunning = false;
    this._favoriteButton?.classList.remove(...this.getTransitionClasses());
  }

  toggleFavorite = async () => {
    if (!this.authController.isLogged 
      || this._updateFavoriteRequestStatus?.isLoading
      || this.animationRunning) return;

    this.isFavorite = !this.isFavorite;
    this.showingTooltip = true;
    this.tooltipMessage = this.isFavorite ? this.addedMessage : this.removedMessage;

    this.animationRunning = true;
    this._favoriteButton?.classList.add(...this.getTransitionClasses(this.isFavorite));
    
    const { requestId } = this.favoriteController.updateFavorite(this.contentId);
    this.updateFavoriteRequestId = requestId;
    this.handleButtonClick();
  };

  handleButtonClick = () => {
    sendEventFavoriteButtonClick(this.contentId);
  }

  renderTooltip() {
    return html`
      <span class="tooltip ${this.tooltipPosition}${this.showingTooltip ? ' visible' : ''}">
        <span class="tooltip--text">${this.tooltipMessage}</span>
      </span>
    `;
  }

  tryToLogin(){
    sendEventFavoriteButtonClick(this.contentId);
    try {
      showLoginModal('Faça login para adicionar o conteúdo aos seus favoritos',()=>{
        this.toggleFavorite();
      })
    } catch (error) {
      window.location.href = `/login/?f=${this.contentId}`;
    }
  }

  renderFavoriteButtonLoginRedirect() {
    return html`
      <button class="favorite-button" tabindex="0" role="button" @click=${this.tryToLogin}>
        <span class="icon"></span>
        <span class="text">${this.addMessage}</span>
        <span class="short-text">${this.isFavorite ? '' : this.addShortMessage}</span>
        ${this.renderTooltip()}
      </button>
    `;
  }

  protected render(): unknown {
    if (!this.authController.isLogged) return this.renderFavoriteButtonLoginRedirect();
    if (this._checkIsFavoriteRequestStatus?.isLoading) return nothing;
    
    return html`
      <span 
        id="favorite-button-${this.id}" 
        class="favorite-button" 
        tabindex="0" 
        role="button" 
        @click=${this.toggleFavorite}
        @animationend=${this.clearAnimation}
      >
        <span class="icon"></span>
        <span class="text">${this.animationRunning ? 
          this.isFavorite ? this.addedMessage : this.removedMessage : 
          this.isFavorite ? this.removeMessage : this.addMessage}</span>
        <span class="short-text">${this.isFavorite ? '' : this.addShortMessage}</span>
        ${this.renderTooltip()}
      </span>
    `;
  }
}